import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  TextField,
  Button,
  Grid,
  FormHelperText,
  FormControl,
  Typography,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TablePagination,
  InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { NotificationManager } from "react-notifications";
import ProcessTestSettingPopup from "./ProcessTestSettingPopup";
import TestCodeSelectorDialog from "../../common/TestCodeSelectorDialog";
import CommonLoader from "../../common/CommonLoader";
import { useNavigate } from "react-router-dom";
import {
  SaveTestProcess,
  UpdateTestProcess,
  GetTestProcess,
} from "../../../services/TestProcessService";

const AddTestProcess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isUpdateMode = id && id !== "add";

  const [processId, setProcessId] = useState("");
  const [description, setDescription] = useState("");
  const [recordid, setRecordid] = useState(null);
  const [processtype, setProcesstype] = useState("");
  const [sampleidentity, setSampleidentity] = useState("");
  const [processTestSettings, setProcessTestSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [prefilledData, setPrefilledData] = useState(null);

  // Test Code Selector Dialog state
  const [testDialogOpen, setTestDialogOpen] = useState(false);

  // Search and pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Process type options
  const processTypeOptions = [
    { value: 0, label: "Validation" },
    { value: 1, label: "Billing" },
    { value: 2, label: "Import" },
    { value: 3, label: "Reporting" },
    { value: 4, label: "Other" },
  ];

  useEffect(() => {
    if (isUpdateMode) {
      loadTestProcess();
    }
  }, [id, isUpdateMode]);

  // Reset page when search term changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const loadTestProcess = async () => {
    setLoading(true);
    try {
      const response = await GetTestProcess(id);
      if (response.statusCode === 200 && response.results) {
        const data = response.results;
        setProcessId(data.processId || "");
        setDescription(data.description || "");
        setRecordid(data.recordid || null);
        setProcesstype(data.processtype !== null && data.processtype !== undefined ? data.processtype : "");
        setSampleidentity(data.sampleidentity || "");
        setProcessTestSettings(data.processTestSettings || []);
      } else {
        throw new Error("Failed to load test process");
      }
    } catch (error) {
      NotificationManager.error(error.message || "Error loading test process");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/vlp/test-process-configuration");
  };

  // Filter data based on search term
  const filteredSettings = useMemo(() => {
    if (!searchTerm || !processTestSettings) return processTestSettings;

    const lowerSearchTerm = searchTerm.toLowerCase();

    return processTestSettings.filter((setting) => {
      if (!setting) return false;

      const searchableFields = [
        setting.vlpTestCode,
        setting.processTestCode,
        setting.parameter,
        setting.methodCode,
        setting.testUnits,
        setting.costOfAnalysis,
      ];

      return searchableFields.some(
        (field) =>
          field && field.toString().toLowerCase().includes(lowerSearchTerm)
      );
    });
  }, [processTestSettings, searchTerm]);

  // Get paginated data
  const paginatedSettings = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredSettings.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredSettings, page, rowsPerPage]);

  const validate = () => {
    const newErrors = {};
    if (!processId) newErrors.processId = "Process ID is required";
    if (!description) newErrors.description = "Description is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const processData = {
      processId,
      description,
      recordid: recordid ? parseInt(recordid) : null,
      processtype: processtype !== "" ? parseInt(processtype) : null,
      sampleidentity,
      processTestSettings: isUpdateMode ? processTestSettings : [], // Only include test settings in update mode
    };

    setLoading(true);
    try {
      let response;
      if (isUpdateMode) {
        response = await UpdateTestProcess(processData);
        if (response.status === 200) {
          NotificationManager.success("Test process updated successfully");
        } else {
          throw new Error("Failed to update process");
        }
      } else {
        // For new process, save only basic information first
        response = await SaveTestProcess(processData);
        if (response.status === 200) {
          NotificationManager.success("Test process saved successfully");
          // Redirect to update mode with the process ID
          navigate(`/vlp/test-process-configuration/${processId}`);
        } else {
          throw new Error("Failed to save process");
        }
      }
    } catch (error) {
      NotificationManager.error(
        error.message ||
          (isUpdateMode ? "Error updating process" : "Error saving process")
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle "Assign Test" button click
  const handleAssignTest = () => {
    setTestDialogOpen(true);
  };

  // Handle manual "Add Test" button click (without pre-selection)
  const handleAddSetting = () => {
    setEditingSetting(null);
    setPrefilledData(null);
    setIsPopupOpen(true);
  };

  const handleEditSetting = (setting) => {
    setEditingSetting(setting);
    setPrefilledData(null);
    setIsPopupOpen(true);
  };

  const handleDeleteSetting = (recordid) => {
    setProcessTestSettings((prev) =>
      prev.filter((setting) => setting.recordid !== recordid)
    );
    NotificationManager.success("Test setting removed");
  };

  const handleSaveSetting = (settingData) => {
    if (editingSetting) {
      // Update existing setting
      setProcessTestSettings((prev) =>
        prev.map((setting) =>
          setting.recordid === editingSetting.recordid ? settingData : setting
        )
      );
      NotificationManager.success("Test setting updated");
    } else {
      // Add new setting
      setProcessTestSettings((prev) => [...prev, settingData]);
      NotificationManager.success("Test setting added");
    }
  };

  // Handle test selection from TestCodeSelectorDialog
  const handleTestSelection = (selectedTest) => {
    if (selectedTest) {
      // Create prefilled data from selected test
      const prefilledTestData = {
        vlpTestCode: selectedTest.testCode || "",
        parameter: selectedTest.parameter || "",
        methodCode: selectedTest.methodCode || "",
        minControlLimit: selectedTest.minControlLimit || "",
        maxControlLimit: selectedTest.maxControlLimit || "",
        costOfAnalysis: selectedTest.costOfAnalysis || "",
        testUnits: selectedTest.testUnits || "",
        // Initialize other required fields
        processTestCode: "",
        processId: processId,
        sortOrder: "",
        recordid: 0,
        isLimit: false,
        sampleLocationId: "",
      };

      setPrefilledData(prefilledTestData);
      setEditingSetting(null);
      setIsPopupOpen(true);
    }
    setTestDialogOpen(false);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Paper elevation={3} sx={{ padding: 3, margin: "auto" }}>
        <Typography variant="h5" gutterBottom>
          {isUpdateMode ? "Update Test Process" : "Add Test Process"}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Process ID"
              value={processId}
              onChange={(e) => setProcessId(e.target.value)}
              fullWidth
              required
              size="small"
              disabled={isUpdateMode}
              error={!!errors.processId}
              helperText={errors.processId}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              required
              size="small"
              error={!!errors.description}
              helperText={errors.description}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="process-type-label">Process Type</InputLabel>
              <Select
                labelId="process-type-label"
                id="process-type-select"
                value={processtype}
                label="Process Type"
                onChange={(e) => setProcesstype(e.target.value)}
              >
                {processTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Sample Identity"
              value={sampleidentity}
              onChange={(e) => setSampleidentity(e.target.value)}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>

        {/* Test Settings Section - Only show in update mode */}
        {isUpdateMode && (
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="h6">Process Tests</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AssignmentIcon />}
                  onClick={handleAssignTest}
                >
                  Assign Test
                </Button>
              </Box>
            </Box>

            {/* Search Box */}
            {processTestSettings.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Search process tests"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  size="small"
                  sx={{ minWidth: 300 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClearSearch}
                          size="small"
                          sx={{ padding: "4px" }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {searchTerm && (
                  <Typography
                    variant="caption"
                    sx={{ ml: 1, color: "text.secondary" }}
                  >
                    Showing {filteredSettings.length} of{" "}
                    {processTestSettings.length} results
                  </Typography>
                )}
              </Box>
            )}

            {processTestSettings.length > 0 && (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[
                          "VLP Test Code",
                          "Process Test Code",
                          "Parameter",
                          "Method Code",
                          "Test Units",
                          "Cost",
                          "Actions",
                        ].map((header) => (
                          <TableCell
                            key={header}
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.primary.main,
                              color: "white",
                              fontWeight: "bold",
                            }}
                          >
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedSettings.length > 0 ? (
                        paginatedSettings.map((setting, index) => (
                          <TableRow
                            key={setting.recordid}
                            sx={{
                              backgroundColor:
                                index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                            }}
                          >
                            <TableCell>{setting.vlpTestCode || "-"}</TableCell>
                            <TableCell>
                              {setting.processTestCode || "-"}
                            </TableCell>
                            <TableCell>{setting.parameter || "-"}</TableCell>
                            <TableCell>{setting.methodCode || "-"}</TableCell>
                            <TableCell>{setting.testUnits || "-"}</TableCell>
                            <TableCell>{setting.costOfAnalysis || "-"}</TableCell>
                            <TableCell>
                              <Box sx={{ minWidth: "80px" }}>
                                <IconButton
                                  onClick={() => handleEditSetting(setting)}
                                  sx={{ color: "orange" }}
                                  size="small"
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  onClick={() =>
                                    handleDeleteSetting(setting.recordid)
                                  }
                                  sx={{ color: "red" }}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            sx={{ textAlign: "center", py: 3 }}
                          >
                            <Typography color="textSecondary">
                              {searchTerm
                                ? "No results found for your search."
                                : "No test settings available."}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                {filteredSettings.length > 0 && (
                  <TablePagination
                    component="div"
                    count={filteredSettings.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      borderTop: "1px solid",
                      borderColor: "divider",
                    }}
                  />
                )}
              </>
            )}

            {processTestSettings.length === 0 && (
              <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
                <Typography color="textSecondary" sx={{ mb: 2 }}>
                  No test settings added yet.
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {/* Show informational message in add mode */}
        {!isUpdateMode && (
          <Box sx={{ mt: 3 }}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 3, 
                textAlign: "center",
                backgroundColor: "info.light",
                color: "info.contrastText"
              }}
            >
              <Typography variant="body1" sx={{ mb: 1 }}>
                ℹ️ Save Process Information First
              </Typography>
              <Typography variant="body2">
                After saving the basic process information, you'll be able to add and manage process tests.
              </Typography>
            </Paper>
          </Box>
        )}

        <Grid container justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
          <Grid item>
            <Button onClick={handleCancel} variant="outlined" color="secondary">
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button onClick={handleSave} variant="contained" color="primary">
              {isUpdateMode ? "Update" : "Save & Continue"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Test Code Selector Dialog - Only in update mode */}
      {isUpdateMode && (
        <TestCodeSelectorDialog
          open={testDialogOpen}
          onClose={() => setTestDialogOpen(false)}
          multiSelect={false}
          onSelect={handleTestSelection}
        />
      )}

      {/* Process Test Setting Popup - Only in update mode */}
      {isUpdateMode && (
        <ProcessTestSettingPopup
          open={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          onSave={handleSaveSetting}
          editingSetting={editingSetting}
          existingSettings={processTestSettings}
          prefilledData={prefilledData}
        />
      )}

      <CommonLoader loading={loading} />
    </>
  );
};

export default AddTestProcess;